import { useState, useEffect, FormEvent } from 'react';
import { auth, db } from '../../firebase';
import { Store, Mail, Lock, Trash2, Plus, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

interface Vendor {
  uid: string;
  email: string;
  storeName: string;
  storeDescription?: string;
  stripeOnboarded?: boolean;
}

export function VendorsManager({ onMessageClick }: { onMessageClick?: () => void }) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeDescription, setStoreDescription] = useState('');

  const fetchVendors = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/vendors', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleCreateVendor = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/create-vendor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          password,
          storeName,
          storeDescription,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Vendor created successfully');
        setEmail('');
        setPassword('');
        setStoreName('');
        setStoreDescription('');
        setShowForm(false);
        fetchVendors();
      } else {
        toast.error(data.error || 'Failed to create vendor');
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteVendor = async (uid: string) => {
    if (!confirm('Are you sure you want to delete this vendor? This will also delete their authentication account.')) {
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/vendors/${uid}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        toast.success('Vendor deleted');
        setVendors(vendors.filter(v => v.uid !== uid));
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to delete vendor');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleStartChat = async (vendor: Vendor) => {
    if (!auth.currentUser) return;
    
    try {
      // Check if thread to ADMIN already exists
      const q = query(
        collection(db, 'chat_threads'), 
        where('customerId', '==', 'ADMIN'),
        where('vendorId', '==', vendor.uid)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        const threadData = {
          customerId: 'ADMIN',
          customerName: 'Platform Support',
          vendorId: vendor.uid,
          vendorName: vendor.storeName || 'Vendor',
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
          readStatus: {
            [auth.currentUser.uid]: true,
            'ADMIN': true // admin is creating it
          }
        };
        await addDoc(collection(db, 'chat_threads'), threadData);
      }
      
      toast.success("Ready to message vendor");
      if (onMessageClick) {
        onMessageClick();
      }
    } catch (e) {
      console.error(e);
      toast.error('Could not start admin chat');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-neutral-400 dark:text-neutral-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white">Vendor Management</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Create and manage official vendor accounts.</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-4 py-2 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-sm"
        >
          {showForm ? 'Cancel' : <><Plus className="w-4 h-4" /> Create New Vendor</>}
        </button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-neutral-900 p-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-6">New Vendor Details</h3>
          <form onSubmit={handleCreateVendor} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 outline-none text-sm transition-colors"
                  placeholder="vendor@example.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Initial Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 outline-none text-sm transition-colors"
                  placeholder="Optional (if left blank, vendor uses single sign-on)"
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Store Name
                </label>
                <input
                  type="text"
                  required
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 outline-none text-sm transition-colors"
                  placeholder="e.g. Artisan Goods"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Store Description (Optional)</label>
                <input
                  type="text"
                  value={storeDescription}
                  onChange={(e) => setStoreDescription(e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 outline-none text-sm transition-colors"
                  placeholder="Brief description of the store"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-2.5 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
              >
                {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : 'Create Vendor Account'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-800">
              <th className="px-6 py-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Store</th>
              <th className="px-6 py-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Email</th>
              <th className="px-6 py-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Stripe Status</th>
              <th className="px-6 py-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {vendors.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400 italic">
                  No vendors found.
                </td>
              </tr>
            ) : (
              vendors.map((vendor) => (
                <tr key={vendor.uid} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-neutral-900 dark:text-white">{vendor.storeName}</div>
                    {vendor.storeDescription && (
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate max-w-xs">{vendor.storeDescription}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">{vendor.email}</td>
                  <td className="px-6 py-4">
                    {vendor.stripeOnboarded ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/50">
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-200/50 dark:border-neutral-700/50">
                        Not Connected
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => handleStartChat(vendor)}
                      className="text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors inline-block"
                      title="Message Vendor"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteVendor(vendor.uid)}
                      className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors inline-block"
                      title="Delete Vendor"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
